/**
 * Search Provider Registry
 * Central registry for all search providers
 *
 * Open/Closed Principle: Add new providers by registering them here,
 * no need to modify the CommandPalette component
 */

import type { SearchProvider, SearchResult } from '../types';

class ProviderRegistry {
  private providers: Map<string, SearchProvider> = new Map();

  /**
   * Register a new search provider
   */
  register<T>(provider: SearchProvider<T>): void {
    if (this.providers.has(provider.id)) {
      console.warn(`Provider with id "${provider.id}" already exists. Overwriting.`);
    }
    this.providers.set(provider.id, provider as SearchProvider);
  }

  /**
   * Unregister a provider
   */
  unregister(providerId: string): void {
    this.providers.delete(providerId);
  }

  /**
   * Get all registered providers sorted by priority
   */
  getAll(): SearchProvider[] {
    return Array.from(this.providers.values()).sort((a, b) => a.priority - b.priority);
  }

  /**
   * Get a specific provider by ID
   */
  get(providerId: string): SearchProvider | undefined {
    return this.providers.get(providerId);
  }

  /**
   * Search across all providers
   * Returns results grouped by provider
   */
  async searchAll(query: string): Promise<Map<string, SearchResult[]>> {
    const results = new Map<string, SearchResult[]>();
    const providers = this.getAll();

    // Search all providers in parallel
    const searchPromises = providers.map(async (provider) => {
      try {
        const providerResults = await provider.search(query);
        return { providerId: provider.id, results: providerResults };
      } catch (error) {
        console.error(`Error searching provider "${provider.id}":`, error);
        return { providerId: provider.id, results: [] };
      }
    });

    const allResults = await Promise.all(searchPromises);

    // Group results by provider
    for (const { providerId, results: providerResults } of allResults) {
      if (providerResults.length > 0) {
        results.set(providerId, providerResults);
      }
    }

    return results;
  }

  /**
   * Get default results from all providers (for empty query)
   */
  async getDefaultResults(): Promise<Map<string, SearchResult[]>> {
    const results = new Map<string, SearchResult[]>();
    const providers = this.getAll();

    const defaultPromises = providers
      .filter((provider) => provider.getDefaultResults)
      .map(async (provider) => {
        try {
          const providerResults = await provider.getDefaultResults!();
          return { providerId: provider.id, results: providerResults };
        } catch (error) {
          console.error(`Error getting defaults from provider "${provider.id}":`, error);
          return { providerId: provider.id, results: [] };
        }
      });

    const allResults = await Promise.all(defaultPromises);

    for (const { providerId, results: providerResults } of allResults) {
      if (providerResults.length > 0) {
        results.set(providerId, providerResults);
      }
    }

    return results;
  }
}

// Singleton instance
export const providerRegistry = new ProviderRegistry();
